'use client';

import {
  ApertureIcon,
  BookOpenTextIcon,
  CoatHangerIcon,
  DressIcon,
  FolderSimpleIcon,
  ImagesSquareIcon,
  SelectionBackgroundIcon,
  TShirtIcon,
  VideoIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { DobiNavigationMark } from './DobiNavigationMark';

/** One optical weight and two real drawings for navigation, including compact rails. */
const symbols = {
  explore: ApertureIcon,
  projects: FolderSimpleIcon,
  assets: ImagesSquareIcon,
  kagemane: VideoIcon,
  virtualTryOn: TShirtIcon,
  outfitExtension: CoatHangerIcon,
  silhouetteExtension: DressIcon,
  whiteBackground: SelectionBackgroundIcon,
  lookbook: BookOpenTextIcon,
} as const;

export type DobiSymbolName = keyof typeof symbols;

interface DobiSymbolProps {
  name: DobiSymbolName;
  className?: string;
  size?: number;
  active?: boolean;
  appearance?: 'outline' | 'brand';
  /** Compatibility with existing navigation icon slots. Weight is defined by the family. */
  strokeWidth?: number;
}

export function DobiSymbol({ name, className, size, active, appearance = 'outline' }: DobiSymbolProps) {
  const Symbol = symbols[name];
  return (
    <span
      aria-hidden="true"
      className={cn('dobi-symbol', className)}
      data-dobi-symbol={name}
      data-symbol-active={active === undefined ? undefined : String(active)}
      style={size ? { width: size, height: size } : undefined}
    >
      {appearance === 'brand' ? <DobiNavigationMark name={name} /> : <>
        <Symbol className="dobi-symbol-outline" weight="regular" aria-hidden="true" focusable="false" />
        <Symbol className="dobi-symbol-filled" weight="fill" aria-hidden="true" focusable="false" />
      </>}
    </span>
  );
}

function navigationSymbol(name: DobiSymbolName) {
  function NavigationSymbol(props: Omit<DobiSymbolProps, 'name'>) {
    return <DobiSymbol appearance="brand" {...props} name={name} />;
  }
  NavigationSymbol.displayName = `DobiSymbol(${name})`;
  return NavigationSymbol;
}

export const DobiExploreIcon = navigationSymbol('explore');
export const DobiProjectsIcon = navigationSymbol('projects');
export const DobiAssetsIcon = navigationSymbol('assets');

export const DOBI_CREATION_ICONS = {
  kagemane: navigationSymbol('kagemane'),
  virtualTryOn: navigationSymbol('virtualTryOn'),
  outfitExtension: navigationSymbol('outfitExtension'),
  silhouetteExtension: navigationSymbol('silhouetteExtension'),
  whiteBackground: navigationSymbol('whiteBackground'),
  lookbook: navigationSymbol('lookbook'),
} as const;
